import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import PlatformIcon from '@/components/PlatformIcon'

import { PLATFORMS, fieldCls, type Draft } from './postHelpers'

/** Plan or edit one organic post: platform, one of the athlete's themes, the idea, day and time */
export const PostForm: React.FC<{
  initial: Draft
  themes: { id: string; label: string }[]
  saving?: boolean
  submitLabel: string
  onSubmit: (draft: Draft) => void
  onCancel: () => void
}> = ({ initial, themes, saving, submitLabel, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState<Draft | null>(initial)
  return (
    <div className="mt-2 space-y-2.5 rounded-xl border border-[#C9E9A6] bg-[#F7FEE7] p-3">
      <div className="flex flex-wrap gap-1.5">
        {PLATFORMS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setDraft((d) => d && { ...d, platform: p.id })}
            aria-pressed={draft?.platform === p.id}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold',
              draft?.platform === p.id
                ? 'border-[#0F172A] bg-[#0F172A] text-[#FFFFFF]'
                : 'border-border bg-card text-foreground',
            )}
          >
            <PlatformIcon platform={p.id} className="h-3.5 w-3.5" /> {p.label}
          </button>
        ))}
      </div>
      {themes.length ? (
        <div className="flex flex-wrap gap-1.5">
          {themes.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setDraft((d) => d && { ...d, theme: { id: t.id, label: t.label } })}
              aria-pressed={draft?.theme?.id === t.id}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-semibold',
                draft?.theme?.id === t.id
                  ? 'border-[#C0FD72] bg-[#C0FD72] text-[#0F172A]'
                  : 'border-border bg-card text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setDraft((d) => d && { ...d, theme: null })}
            aria-pressed={!draft?.theme}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-semibold',
              !draft?.theme
                ? 'border-[#C0FD72] bg-[#C0FD72] text-[#0F172A]'
                : 'border-border bg-card text-foreground',
            )}
          >
            Something else
          </button>
        </div>
      ) : null}
      <textarea
        value={draft?.idea ?? ''}
        onChange={(e) => setDraft((d) => d && { ...d, idea: e.target.value })}
        placeholder="The idea in a line, e.g. 6am serving drill, then the coffee"
        className={`${fieldCls} min-h-16 resize-none`}
      />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs font-semibold text-muted-foreground">
          Day
          <input
            type="date"
            value={draft?.plannedOn ?? ''}
            onChange={(e) => setDraft((d) => d && { ...d, plannedOn: e.target.value })}
            className={`${fieldCls} mt-1`}
          />
        </label>
        <label className="text-xs font-semibold text-muted-foreground">
          Time (optional)
          <input
            type="time"
            value={draft?.plannedTime ?? ''}
            onChange={(e) => setDraft((d) => d && { ...d, plannedTime: e.target.value })}
            className={`${fieldCls} mt-1`}
          />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={saving || (!draft?.theme && !draft?.idea.trim())}
          onClick={() => draft && onSubmit(draft)}
          className="h-11 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] hover:bg-[#B2F25E] disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-11 rounded-xl border border-border bg-card font-semibold text-foreground hover:bg-muted"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
